import { useState } from "react";
import { useMCPTT } from "../mcptt/hooks";
import { BRAND_TAGLINE, BRAND_TITLE } from "../config";
import type { Network } from "@mcptt/client";
import { AudioMeter } from "./AudioMeter";

interface Props {
  networks:             ReadonlyArray<Network>;
  currentNetworkId:     string | null;
  onSelectNetwork:      (id: string) => void;

  /** Currently-selected talkgroup (lifted from the Shell). */
  selectedTgName:       string | null;
  selectedTgState:      "rx" | "tx" | "hangtime" | "idle";
  selectedTgLastTalker: string | undefined;
  onClearSelection:     () => void;

  /** Spacebar-held TX. Renders the channel card pulsing red. When
   *  audioState !== "ready" the badge says "VISUAL"; otherwise "LIVE". */
  isVisualTx:           boolean;

  /** DIAG panel toggle (lifted from Shell). */
  diagOpen:             boolean;
  onToggleDiag:         () => void;
}

/** App bar — brand + selected-network subtitle + selected-channel
 *  card + actions. */
export function AppBar({
  networks, currentNetworkId, onSelectNetwork,
  selectedTgName, selectedTgState, selectedTgLastTalker,
  onClearSelection, isVisualTx,
  diagOpen, onToggleDiag,
}: Props) {
  const {
    signOut, snapshot,
    enableAudio, disableAudio, setAudioCodec,
    setAudioVolume, setAudioMuted,
  } = useMCPTT();
  const current = networks.find((n) => n.ID === currentNetworkId);

  const isLive = isVisualTx && snapshot.audioState === "ready";

  return (
    <div className="appbar">
      <div className="appbar-brand">
        <img src="/build/mcptt-logo.png" alt="MCPTT" />
        <div className="appbar-titles">
          <div className="appbar-title">{BRAND_TITLE} DISPATCHER</div>
          <div className="appbar-sub">{BRAND_TAGLINE}</div>
        </div>
      </div>

      {networks.length > 0 ? (
        <NetworkPicker
          networks={networks}
          currentId={currentNetworkId}
          onSelect={onSelectNetwork}
        />
      ) : null}

      {/* Persistent indicator of which TG the operator has armed. */}
      <SelectedChannelCard
        name={selectedTgName}
        state={selectedTgState}
        lastTalker={selectedTgLastTalker}
        onClear={onClearSelection}
        isVisualTx={isVisualTx}
        isLive={isLive}
      />

      <AudioToggle
        state={snapshot.audioState}
        codec={snapshot.audioCodec}
        volume={snapshot.audioVolume}
        muted={snapshot.audioMuted}
        error={snapshot.audioError}
        isVisualTx={isVisualTx}
        onEnable={enableAudio}
        onDisable={disableAudio}
        onSetCodec={setAudioCodec}
        onSetVolume={setAudioVolume}
        onSetMuted={setAudioMuted}
      />

      <div className="appbar-spacer" />

      <button
        type="button"
        className={`appbar-action${diagOpen ? " is-on" : ""}`}
        onClick={onToggleDiag}
        title="Toggle diagnostics overlay (Ctrl+Shift+D)"
      >
        DIAG
      </button>
      <button type="button" className="appbar-action sos" title="Trigger SOS — not implemented yet">
        SOS
      </button>
      <button type="button" className="appbar-action" onClick={signOut}>
        Sign Out
      </button>

      {current ? (
        <span className="appbar-sub" style={{ marginLeft: 8, paddingLeft: 12, borderLeft: "1px solid var(--border)" }}>
          {current.Name} &nbsp;·&nbsp; {(current.users?.length ?? 0)} USERS
        </span>
      ) : null}
    </div>
  );
}

function SelectedChannelCard({
  name, state, lastTalker, onClear, isVisualTx, isLive,
}: {
  name:        string | null;
  state:       "rx" | "tx" | "hangtime" | "idle";
  lastTalker:  string | undefined;
  onClear:     () => void;
  isVisualTx:  boolean;
  isLive:      boolean;
}) {
  if (!name) {
    return (
      <div className="appbar-channel appbar-channel--empty"
        title="Click a talkgroup tile to arm it. Hold SPACE to TX. Enable audio to transmit live.">
        <span className="appbar-channel-label">SELECT A CHANNEL</span>
      </div>
    );
  }
  const effectiveState: "rx" | "tx" | "hangtime" | "idle" = isVisualTx ? "tx" : state;
  return (
    <div className={`appbar-channel is-${effectiveState}${isVisualTx ? " is-vtx" : ""}`}>
      <span className="appbar-channel-label">CHANNEL</span>
      <span className="appbar-channel-name" title={name}>{name}</span>
      {isVisualTx ? (
        <span
          className="appbar-channel-state is-tx"
          title={isLive
            ? "Live transmit \u2014 mic open, voice frames going to the manager."
            : "Spacebar TX. Audio is OFF, so no voice frames are sent."}
        >
          {isLive ? "\u25CF TX (LIVE)" : "\u25CF TX (VISUAL)"}
        </span>
      ) : (
        <span className={`appbar-channel-state is-${state}`}>
          {state === "tx" ? "\u25CF TX"
           : state === "rx" ? "\u25CF RX"
           : state === "hangtime" ? "\u25CF HANG"
           : lastTalker ? "IDLE" : "READY"}
        </span>
      )}
      {lastTalker && !isVisualTx
        ? <span className="appbar-channel-last" title={`Last talker: ${lastTalker}`}>{lastTalker}</span>
        : null}
      <button
        type="button"
        className="appbar-channel-close"
        onClick={onClear}
        title="Clear channel selection"
        aria-label="Clear channel selection"
      >
        {"\u2715"}
      </button>
    </div>
  );
}

function AudioToggle({
  state, codec, volume, muted, error, isVisualTx,
  onEnable, onDisable, onSetCodec, onSetVolume, onSetMuted,
}: {
  state:        "off" | "enabling" | "ready" | "denied" | "error";
  codec:        "mulaw" | "alaw";
  volume:       number;
  muted:        boolean;
  error:        string | null;
  isVisualTx:   boolean;
  onEnable:     () => Promise<void>;
  onDisable:    () => Promise<void>;
  onSetCodec:   (c: "mulaw" | "alaw") => void;
  onSetVolume:  (v: number) => void;
  onSetMuted:   (b: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const tone = state === "ready"    ? "ok"
             : state === "enabling" ? "pending"
             : state === "denied"   ? "warn"
             : state === "error"    ? "danger"
                                    : "off";
  const label = state === "ready"    ? (muted ? "AUDIO MUTED" : "AUDIO ON")
              : state === "enabling" ? "ENABLING\u2026"
              : state === "denied"   ? "AUDIO RX (NO MIC)"
              : state === "error"    ? "AUDIO ERROR"
                                     : "AUDIO OFF";
  const handleClick = async () => {
    try {
      if (state === "off" || state === "error") await onEnable();
      else if (state === "ready" || state === "denied") setOpen((v) => !v);
    } catch { /* state already reflects */ }
  };
  // The TX meter only makes sense while the operator is actually
  // transmitting (and audio is "ready"). Otherwise show the RX meter.
  const meterMode: "rx" | "tx" = isVisualTx && state === "ready" ? "tx" : "rx";

  return (
    <div className="appbar-audio-wrap" style={{ position: "relative" }}>
      <button
        type="button"
        className={`appbar-audio appbar-audio--${tone}${muted ? " is-muted" : ""}`}
        onClick={handleClick}
        title={
          state === "off"
            ? "Click to enable audio (RX playback + TX mic). Browsers require a user gesture to start audio."
            : state === "denied"
              ? `Microphone access denied. Playback works; transmit does not. ${error ?? ""}`
              : state === "error"
                ? `Audio error: ${error ?? "unknown"}`
                : muted
                  ? "Audio is MUTED. Click to manage; the speaker icon un-mutes."
                  : `Audio ON \u2014 codec ${codec.toUpperCase()}. Click to manage.`
        }
      >
        <SpeakerIcon muted={muted || state === "off"} />
        <span className="appbar-audio-label">{label}</span>
        {(state === "ready" || state === "denied") ? <AudioMeter mode={meterMode} /> : null}
      </button>
      {open ? (
        <div className="appbar-audio-menu" onMouseLeave={() => setOpen(false)}>
          <div className="appbar-audio-menu-head">VOLUME</div>
          <div className="appbar-audio-volume">
            <button
              type="button"
              className="appbar-audio-mute"
              onClick={() => onSetMuted(!muted)}
              title={muted ? "Un-mute (restore previous volume)" : "Mute (preserves volume)"}
              aria-label={muted ? "Un-mute" : "Mute"}
            >
              <SpeakerIcon muted={muted} />
            </button>
            <input
              type="range"
              min={0}
              max={100}
              value={Math.round((muted ? 0 : volume) * 100)}
              onChange={(e) => {
                const v = Number(e.target.value) / 100;
                if (muted && v > 0) onSetMuted(false);
                onSetVolume(v);
              }}
              className="appbar-audio-slider"
              aria-label="Master volume"
            />
            <span className="appbar-audio-pct">{Math.round((muted ? 0 : volume) * 100)}%</span>
          </div>

          <div className="appbar-audio-menu-sep" />
          <div className="appbar-audio-menu-head">CODEC</div>
          <button
            type="button"
            className={`appbar-audio-menu-item ${codec === "mulaw" ? "selected" : ""}`}
            onClick={() => { onSetCodec("mulaw"); setOpen(false); }}
          >
            {"\u03BC"}-LAW (PCMU) — default, North America
          </button>
          <button
            type="button"
            className={`appbar-audio-menu-item ${codec === "alaw" ? "selected" : ""}`}
            onClick={() => { onSetCodec("alaw"); setOpen(false); }}
          >
            A-LAW (PCMA) — most non-NA WalkieFleet
          </button>
          <div className="appbar-audio-menu-sep" />
          <button
            type="button"
            className="appbar-audio-menu-item danger"
            onClick={async () => { await onDisable(); setOpen(false); }}
          >
            DISABLE AUDIO
          </button>
        </div>
      ) : null}
    </div>
  );
}

/** Compact speaker icon. Renders the standard speaker cone with three
 *  sound waves; when `muted` is true, draws an X over the waves. */
function SpeakerIcon({ muted }: { muted: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ display: "block", flexShrink: 0 }}
    >
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" fill="currentColor" stroke="none" />
      {muted ? (
        <>
          <line x1="22" y1="9" x2="16" y2="15" />
          <line x1="16" y1="9" x2="22" y2="15" />
        </>
      ) : (
        <>
          <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
          <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
        </>
      )}
    </svg>
  );
}

function NetworkPicker({
  networks,
  currentId,
  onSelect,
}: {
  networks: ReadonlyArray<Network>;
  currentId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <select
      value={currentId ?? ""}
      onChange={(e) => onSelect(e.target.value)}
      style={{
        height: 36,
        padding: "0 12px",
        background: "var(--navy-650)",
        color: "var(--bone)",
        border: "1px solid var(--border)",
        borderRadius: "var(--r-chip)",
        fontFamily: "var(--mono)",
        fontSize: 12.5,
        fontWeight: 700,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
      }}
    >
      {networks.map((n) => (
        <option key={n.ID} value={n.ID}>
          {n.Name}
        </option>
      ))}
    </select>
  );
}
